import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import {
  BodyText,
  Button,
  Form,
  theme,
  TYPE_CONFIG,
  useResponsive,
} from '@sg/garnish';

import {
  useRedeemGiftCardAutoSubmit,
  useRedeemGiftCardForm,
} from '../../hooks';

export const GiftCardRedemptionForm = (props: GiftCardFormProps) => {
  const {
    style,
    giftCardCode = '',
    giftCardRegistrationCode = '',
    onRedeemSuccess,
  } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  const { form, submitForm } = useRedeemGiftCardForm({ onRedeemSuccess });

  const { control, formState, reset: resetForm } = form;
  const { isSubmitting, dirtyFields } = formState;
  const canRedeemGiftCard =
    isSubmitting ||
    (dirtyFields.giftCardCode && dirtyFields.giftCardRegistrationCode);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useFocusEffect(useCallback(() => resetForm, [resetForm]));
  useRedeemGiftCardAutoSubmit({
    giftCardCode,
    giftCardRegistrationCode,
    form,
    submitForm,
  });

  return (
    <View style={[styles.container, style]}>
      <View
        style={match([styles.contentXS, styles.contentXS, styles.contentLG])}
      >
        <View style={styles.fieldContainer}>
          <Form.TextFieldWithBackground
            name="giftCardCode"
            control={control}
            rules={{ required: true }}
            labelStyle={styles.upperCaseLabel}
            label={formatMessage(messages.giftCardNumber)}
            placeholder={formatMessage(messages.giftCardNumberPlaceholder)}
            accessibilityLabel={formatMessage(messages.giftCardNumber)}
            hideNoticeSpace
            hideSubmitButton
            disabled={isSubmitting}
            onSubmit={submitForm}
          />
        </View>

        <View style={styles.fieldContainer}>
          <Form.TextFieldWithBackground
            name="giftCardRegistrationCode"
            control={control}
            rules={{ required: true }}
            labelStyle={styles.upperCaseLabel}
            label={formatMessage(messages.registrationCode)}
            placeholder={formatMessage(messages.registrationCodePlaceholder)}
            accessibilityLabel={formatMessage(messages.registrationCode)}
            hideSubmitButton
            hideNoticeSpace
            disabled={isSubmitting}
            onSubmit={submitForm}
          />
        </View>

        <Button
          palette="spinach"
          size={match(['large', 'large', 'small'])}
          style={styles.cta}
          accessibilityLabel={formatMessage(messages.ctaA11yLabel)}
          isLoading={isSubmitting}
          disabled={!canRedeemGiftCard}
          onPress={submitForm}
        >
          <FormattedMessage {...messages.ctaLabel} />
        </Button>
      </View>

      <BodyText sizeMatch={['14']}>
        <FormattedMessage {...messages.giftCardDisclaimer} />
      </BodyText>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.large,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    padding: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  contentXS: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  contentLG: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing['4'],
  },
  fieldContainer: {
    flexGrow: 1,
  },
  cta: {
    minWidth: 120,
    marginTop: theme.spacing['4'],
  },
  upperCaseLabel: {
    ...TYPE_CONFIG.LABEL['10'],
    textTransform: 'uppercase',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Redeem gift card',
    description: 'Gift Card Redemption | Title',
  },
  giftCardNumber: {
    defaultMessage: 'Gift card number',
    description: 'Gift Card Redemption | Gift card number',
  },
  giftCardNumberPlaceholder: {
    defaultMessage: 'Enter number',
    description: 'Gift Card Redemption | Gift card number placeholder',
  },
  registrationCode: {
    defaultMessage: 'Registration code',
    description: 'Gift Card Redemption | Registration code',
  },
  registrationCodePlaceholder: {
    defaultMessage: 'Enter code',
    description: 'Gift Card Redemption | Registration code placeholder',
  },
  ctaLabel: {
    defaultMessage: 'Redeem',
    description: 'Gift Card Redemption | CTA label',
  },
  ctaA11yLabel: {
    defaultMessage: 'Redeem gift card',
    description: 'Gift Card Redemption | CTA A11y label',
  },
  giftCardDisclaimer: {
    defaultMessage:
      'If entering gift card number, your gift card amount will be transferred to your online credit card and you will no longer be able to use your physical gift card.',
    description: 'Gift Card Redemption | Gift card disclaimer',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardFormProps = {
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
  style?: StyleProp<ViewStyle>;
  onRedeemSuccess?: () => void;
};
